import { FC, useCallback } from 'react';
import { useRouter } from 'shared-routing';

import SectionContainer from '@common/components/SectionContainer';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { openJourneyPickerDates } from '@features/JourneyPickerV2/openJourneyPickerDates';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useJourneyPickerAvailability } from '@features/JourneyPickerV2/useJourneyPickerAvailability';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useLabels } from '@features/Menu/useLabels';
import { useRatePageSettings } from '@features/rates';
import { RatesFilterType } from '@features/rates/types';
import { useGetSpaStaysPage } from '@features/spa/hooks';
import { useTranslations } from '@lang';
import { useRatesErrorMessage } from '@common/hooks/useRatesErrorMessage';
import { useCouldNotCheckFeedback } from '@common/hooks/useCouldNotCheckFeedback';
import useDetailModalLabels from '@common/hooks/useDetailModalLabels';

import { AccommodationListTemplate } from 'voyager';
import type { JourneyPickerSearchParams } from 'voyager';

import { parseDateToString } from '@utils/helpers';
import { queryParamToString } from '@utils/queryParam';
import { useRatesList } from './useRatesList';
import { RatesV2Props } from './RatesV2.types';

export const RatesV2: FC<RatesV2Props> = ({
  journeyParams,
  language,
  changeJourneyParam,
  property,
  type,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const labels = useLabels();
  const detailModalLabels = useDetailModalLabels();

  const isSpaStays = type === RatesFilterType.SPA_STAYS;
  const listRoute = isSpaStays ? Routes.SPA_STAYS_LIST : Routes.RATE_LIST;
  const detailRoute = isSpaStays ? Routes.SPA_STAYS_DETAIL : Routes.RATE_DETAIL;

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  const {
    listItems: ratedItems,
    inclusionOptions,
    isFetching,
    error,
    isAvailabilityUnknown,
    isRetrying,
    retry,
  } = useRatesList(journeyParams, language, property, type);
  const { content: ratesPageContent } = useRatePageSettings(
    language,
    t('ratesListing.sectionTitle')
  );
  const { data: spaStaysPage } = useGetSpaStaysPage(
    property?.id,
    language,
    isSpaStays && !!property?.id
  );
  const errorContent = useRatesErrorMessage(error);
  const listItems = isAvailabilityUnknown ? [] : ratedItems;
  const noResultsFeedback = useCouldNotCheckFeedback(isAvailabilityUnknown, retry, isRetrying);
  const noResultsLabel = isAvailabilityUnknown
    ? t('spa.integrationUnavailable')
    : error
    ? errorContent.message
    : t('ratesListing.noResults');

  const titleLabel = isSpaStays
    ? spaStaysPage?.title ?? labels?.spastays ?? t('flynav.spaStays')
    : ratesPageContent.title;

  const { availability, isLoadingAvailability, onMonthChange } = useJourneyPickerAvailability({
    adults: journeyParams.adults ?? 0,
    children: journeyParams.children ?? 0,
    initialMonth: journeyParams.fromDate?.format('YYYY-MM-DD'),
  });

  const handleSearchComplete = useCallback(
    (params: JourneyPickerSearchParams) => {
      const adults = params.adults;
      const children = params.children ?? 0;
      const promoRateCode = queryParamToString(router.query.promoRateCode);
      const rateCode = queryParamToString(router.query.rateCode);
      const fromDate = parseDateToString(params.fromDate);

      changeJourneyParam('rooms', [{ roomNumber: 1, adults, children }]);
      changeJourneyParam('childAges', []);
      // The spa half of a spa stay is booked on the arrival date.
      if (isSpaStays) changeJourneyParam('spaDate', params.fromDate);

      router.push({
        pathname: listRoute,
        query: {
          fromDate,
          toDate: params.toDate ? parseDateToString(params.toDate) : undefined,
          adults: String(adults),
          children: String(children),
          ...(isSpaStays ? { spaDate: fromDate } : undefined),
          ...(promoRateCode ? { promoRateCode } : undefined),
          ...(rateCode ? { rateCode } : undefined),
        },
      });
    },
    [router, changeJourneyParam, isSpaStays, listRoute]
  );

  const handleEditSearch = useCallback(() => {
    openJourneyPickerDates();
  }, []);

  const promoRateCode =
    queryParamToString(router.query.promoRateCode) ?? journeyParams.promoRateCode ?? '';

  const handleRateCodeChange = useCallback(
    (nextCode: string) => {
      const rateCode = queryParamToString(router.query.rateCode);
      const trimmed = nextCode.trim();
      changeJourneyParam('promoRateCode', trimmed || undefined);
      router.push({
        pathname: listRoute,
        query: {
          ...router.query,
          promoRateCode: trimmed || undefined,
          rateCode: rateCode || undefined,
        },
      });
    },
    [router, changeJourneyParam, listRoute]
  );

  const handleSelectRate = useCallback(
    (id: string) => {
      const promoRateCode =
        queryParamToString(router.query.promoRateCode) ?? journeyParams.promoRateCode ?? undefined;
      const fromDate = parseDateToString(journeyParams.fromDate);

      router.push({
        pathname: detailRoute,
        query: {
          id,
          fromDate,
          toDate: parseDateToString(journeyParams.toDate),
          adults: String(journeyParams.adults),
          children: String(journeyParams.children),
          ...(isSpaStays ? { spaDate: fromDate } : undefined),
          ...(promoRateCode ? { promoRateCode } : undefined),
        },
      });
    },
    [
      router,
      journeyParams.fromDate,
      journeyParams.toDate,
      journeyParams.adults,
      journeyParams.children,
      journeyParams.promoRateCode,
      isSpaStays,
      detailRoute,
    ]
  );

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <JourneyPickerV2
            dateMode="range"
            dateContext="stay"
            showGuests
            categories={navCategories}
            category={isSpaStays ? 'spa-stays' : 'rates'}
            onCategoryChange={handleNavCategoryChange}
            onSearchComplete={handleSearchComplete}
            availability={availability}
            isLoadingAvailability={isLoadingAvailability}
            onMonthChange={onMonthChange}
            showRate
            rateCode={promoRateCode}
            onRateCodeChange={handleRateCodeChange}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <Spinner showDelayMessage isActive={isFetching && listItems.length > 0} />
        <AccommodationListTemplate
          items={listItems}
          onSelectItem={handleSelectRate}
          isLoading={isFetching && listItems.length === 0 && !isRetrying}
          titleLabel={titleLabel}
          fromPriceLabel={t('common.from')}
          soldOutLabel={t('common.unavailable')}
          noResultsLabel={noResultsLabel}
          noResultsFeedback={noResultsFeedback}
          noImageLabel={t('basket.noImageLabel')}
          editSearchLabel={t('common.editSearch')}
          onEditSearch={handleEditSearch}
          quickViewLabels={{
            quickView: t('ratesListing.quickView'),
            inclusions: t('ratesListing.quickView.included'),
            select: t('ratesListing.quickView.book'),
            close: detailModalLabels.close,
            previousImage: detailModalLabels.previousImage,
            nextImage: detailModalLabels.nextImage,
            goToImage: detailModalLabels.goToImage,
            fromPrice: t('common.from'),
          }}
          columns={2}
          inclusionOptions={inclusionOptions}
          inclusionFilterLabel={t('ratesListing.filterLabel')}
          noFilterResultsLabel={t('ratesListing.noFilterResults')}
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
